import { Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useRef, useEffect, useState } from "react";
import { useConfirmDialog } from "@powerdiary/components-library/lib";
import Content from "../Layout/Content";
import { useAppointments } from "./useAppointments";
import NoAppointments from "./NoAppointments";
import { getUsersTimeZone } from "utils/timeZoneUtils";
import LoadingOverlay from "../Shared/LoadingOverlay";
import AppointmentItem from "./AppointmentItem";
import { useCancelAppointment } from "./useCancelAppointment";
import { UpcomingAppointmentDto } from "types/generated/models";
import { useAnalytics } from "hooks/useAnalytics";
import AppointmentRescheduleDialog from "./AppointmentRescheduleDialog";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";

export default function Appointments() {
    const targetAppointmentIdRef = useRef<number | null>(null);
    const [showAppointmentRescheduleDialog, setShowAppointmentRescheduleDialog] = useState(false);
    const [appointmentToReschedule, setAppointmentToReschedule] = useState<UpcomingAppointmentDto | null>(null);
    const [appointments, setAppointments] = useState<Array<UpcomingAppointmentDto>>([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { data, isLoading, refetch } = useAppointments();
    const { cancelAppointment } = useCancelAppointment();
    const { track } = useAnalytics();
    const { confirm } = useConfirmDialog({
        title: "Confirmation",
        content: <Typography variant="body2">Are you sure you want to cancel this appointment?</Typography>,
        allowClose: true,
        onCancel: () => void cancelAppointmentCancellation(),
        onConfirm: () => void performAppointmentCancellation(),
        confirmationText: "Yes",
        cancellationText: "No",
    });

    useEffect(() => {
        track("page_view", { page_title: "Appointments" });
    }, [track]);

    useEffect(() => {
        if (data?.upcomingAppointments) {
            setAppointments(data.upcomingAppointments);
        }
    }, [data]);

    const performAppointmentCancellation = async () => {
        await cancelAppointment(targetAppointmentIdRef.current);

        targetAppointmentIdRef.current = null;
    };

    const requestAppointmentCancellation = (appointmentId: number) => {
        targetAppointmentIdRef.current = appointmentId;
        void confirm();
    };

    const cancelAppointmentCancellation = () => {
        targetAppointmentIdRef.current = null;
    };

    const requestAppointmentReschedule = (appointment: UpcomingAppointmentDto) => {
        track("reschedule_appointment_clicked", {
            appointment_id: appointment.id,
        });

        if (!appointment.serviceAvailableOnline) {
            enqueueSnackbar(
                "This service can not be rescheduled online. Please contact your service provider to reschedule this appointment.",
                { variant: "error" }
            );
            return;
        }

        setAppointmentToReschedule(appointment);
        setShowAppointmentRescheduleDialog(true);
    };

    const closeRescheduleDialog = () => {
        setShowAppointmentRescheduleDialog(false);
        setAppointmentToReschedule(null);
    };

    const handleRescheduleSuccess = () => {
        void refetch();
        closeRescheduleDialog();
    };

    return (
        <Content dataTestIdSuffix="appointments">
            {isLoading ? (
                <LoadingOverlay />
            ) : (
                <Stack spacing={2} p={isMobile ? 2 : 4}>
                    <Stack>
                        <Typography variant="h6" fontWeight="bold">
                            Appointments
                        </Typography>
                        <Typography variant="caption">All times are in {getUsersTimeZone()}</Typography>
                    </Stack>
                    <Paper>
                        <Stack spacing={2} p={isMobile ? 2 : 4}>
                            {appointments && appointments.length > 0 ? (
                                <Stack spacing={1} sx={{ overflow: "hidden", minWidth: 0 }}>
                                    {appointments.map((appointment) => (
                                        <AppointmentItem
                                            key={appointment.id}
                                            appointment={appointment}
                                            requestAppointmentCancellation={requestAppointmentCancellation}
                                            requestAppointmentReschedule={requestAppointmentReschedule}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <NoAppointments />
                            )}
                        </Stack>
                    </Paper>
                </Stack>
            )}

            {showAppointmentRescheduleDialog && (
                <AppointmentRescheduleDialog
                    handleClose={closeRescheduleDialog}
                    handleRescheduleSuccess={handleRescheduleSuccess}
                    appointment={appointmentToReschedule}
                />
            )}
        </Content>
    );
}
